/**
 * Owner: Masa Bayu
 * File: advanced-pwa.ts
 * Date:Time: 2026-09-16 18:41
 * Description: T8-E1 PWA deepen — InstallState · SW shell · NotificationPreference.
 */
import { assertNoForbiddenFoundationKey } from './authority';
import { assertT8E1SecurityIntact, assertT8TrackSecurityIntact } from './advanced-security';
import { createQxreadId } from './id';
import { isSessionAuthenticated } from './identity';
import { assertTimestamp, assertValidId } from './guards';
import { QXREAD_INSTALL_STATES } from './pwa-foundation';
export const QXREAD_NOTIFICATION_CHANNELS = ['product', 'security', 'marketing'];
function assertInstallState(value, label) {
    if (typeof value !== 'string' || !QXREAD_INSTALL_STATES.includes(value)) {
        throw new Error(`${label} must be one of: ${QXREAD_INSTALL_STATES.join(', ')}`);
    }
    return value;
}
function assertChannel(value, label) {
    if (typeof value !== 'string' ||
        !QXREAD_NOTIFICATION_CHANNELS.includes(value)) {
        throw new Error(`${label} must be one of: ${QXREAD_NOTIFICATION_CHANNELS.join(', ')}`);
    }
    return value;
}
/** Create install record. Install never unlocks content or prepares OfflinePackage. */
export function createPwaInstallRecord(input) {
    assertNoForbiddenFoundationKey(input, 'QXRead PWA install create');
    assertT8TrackSecurityIntact();
    assertT8E1SecurityIntact();
    if (input.account !== null) {
        if (!isSessionAuthenticated(input.session) ||
            input.session.accountId !== input.account.accountId) {
            return Object.freeze({
                allowed: false,
                reason: 'Authenticated session must bind account for named InstallState',
            });
        }
        assertValidId(input.account.accountId, 'QXRead PWA accountId');
    }
    const updatedAt = input.updatedAt ?? new Date().toISOString();
    assertTimestamp(updatedAt, 'QXRead PWA install updatedAt');
    const installRecordId = input.installRecordId !== undefined ? input.installRecordId : createQxreadId('qxrdpwa');
    assertValidId(installRecordId, 'QXRead installRecordId');
    return Object.freeze({
        allowed: true,
        install: Object.freeze({
            installRecordId,
            accountId: input.account?.accountId ?? null,
            installState: assertInstallState(input.installState ?? 'unknown', 'QXRead installState'),
            serviceWorkerRegistered: input.serviceWorkerRegistered === true,
            updatedAt,
            unlocksContent: false,
            grantsEntitlement: false,
            preparesOfflinePackage: false,
            cachesPublicationTruth: false,
            executesFlipOrMedia: false,
        }),
    });
}
export function transitionPwaInstallState(input) {
    assertNoForbiddenFoundationKey(input, 'QXRead PWA install transition');
    assertT8E1SecurityIntact();
    const nextState = assertInstallState(input.nextState, 'QXRead install nextState');
    const updatedAt = input.updatedAt ?? new Date().toISOString();
    assertTimestamp(updatedAt, 'QXRead PWA install updatedAt');
    return Object.freeze({
        ...input.install,
        installState: nextState,
        updatedAt,
        unlocksContent: false,
        grantsEntitlement: false,
        preparesOfflinePackage: false,
        cachesPublicationTruth: false,
        executesFlipOrMedia: false,
    });
}
/** Mark shell SW registered — cache ≠ entitlement / publication truth. */
export function registerPwaShellWorker(input) {
    assertNoForbiddenFoundationKey(input, 'QXRead PWA shell worker');
    assertT8E1SecurityIntact();
    const updatedAt = input.updatedAt ?? new Date().toISOString();
    assertTimestamp(updatedAt, 'QXRead PWA SW updatedAt');
    return Object.freeze({
        ...input.install,
        serviceWorkerRegistered: true,
        updatedAt,
        unlocksContent: false,
        grantsEntitlement: false,
        preparesOfflinePackage: false,
        cachesPublicationTruth: false,
        executesFlipOrMedia: false,
    });
}
export function upsertNotificationPreference(input) {
    assertNoForbiddenFoundationKey(input, 'QXRead notification preference');
    assertT8E1SecurityIntact();
    if (!isSessionAuthenticated(input.session) ||
        input.session.accountId !== input.account.accountId) {
        return Object.freeze({
            allowed: false,
            reason: 'NotificationPreference requires authenticated owning session',
        });
    }
    if (typeof input.enabled !== 'boolean') {
        return Object.freeze({ allowed: false, reason: 'enabled must be boolean' });
    }
    const updatedAt = input.updatedAt ?? new Date().toISOString();
    assertTimestamp(updatedAt, 'QXRead notification updatedAt');
    const preferenceId = input.preferenceId !== undefined ? input.preferenceId : createQxreadId('qxrdnpref');
    assertValidId(preferenceId, 'QXRead preferenceId');
    assertValidId(input.account.accountId, 'QXRead notification accountId');
    return Object.freeze({
        allowed: true,
        preference: Object.freeze({
            preferenceId,
            accountId: input.account.accountId,
            channel: assertChannel(input.channel, 'QXRead notification channel'),
            enabled: input.enabled,
            updatedAt,
            inventsEntitlement: false,
            grantsAccess: false,
        }),
    });
}
export function projectPwaInstallSurface(input) {
    assertNoForbiddenFoundationKey(input, 'QXRead PWA install projection');
    assertT8E1SecurityIntact();
    let accessAllowed = true;
    let accessReason = 'PWA install surface available (install ≠ unlock)';
    if (input.account !== null) {
        if (!isSessionAuthenticated(input.session) ||
            input.session.accountId !== input.account.accountId) {
            accessAllowed = false;
            accessReason = 'Session does not bind account for PWA preference projection';
        }
    }
    const projectedAt = input.projectedAt ?? new Date().toISOString();
    assertTimestamp(projectedAt, 'QXRead PWA projectedAt');
    const prefs = input.preferences ?? [];
    return Object.freeze({
        install: input.install,
        preferences: Object.freeze([...prefs]),
        projectedAt,
        accessAllowed,
        accessReason,
        unlocksContent: false,
        grantsEntitlement: false,
        preparesOfflinePackage: false,
    });
}
//# sourceMappingURL=advanced-pwa.js.map